import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Paper from '@material-ui/core/Paper';
import Button from 'components/common/Button';
import FormError from 'components/common/FormError';
import Menu from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';

import { Field, Fields } from 'redux-form';

import DatepickerField from 'components/fields/DatepickerField';
import TextField from 'components/fields/TextField';
import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import CheckboxField from 'components/fields/CheckboxField';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';
import SelectAutosuggestField from 'components/fields/SelectAutosuggestField';
import Link from 'components/common/Link';
import IconRefresh from 'components/icons/IconRefresh';
import IconCopy from 'components/icons/IconCopy';
import IconTrashCan from 'components/icons/IconTrashCan';
import ReportNameCreateDialog from 'components/reports/ReportNameCreateDialog';

import {
  requestFetchReports,
  requestSearchReports,
  deleteReport,
} from 'redux/modules/reports';
import {
  requestFetchHCPCSCodes,
  requestSearchHCPCSCodes,
} from 'redux/modules/hcpcsCodes';
import { requestSearchUsers } from 'redux/modules/users';
import {
  requestFetchPrescribers,
  requestSearchPrescribers,
} from 'redux/modules/prescribers';
import {
  requestFetchSalesRepresentatives,
  requestSearchSalesRepresentatives,
} from 'redux/modules/salesRepresentatives';
import {
  requestFetchReferralSources,
  requestSearchReferralSources,
} from 'redux/modules/referralSources';
import {
  requestFetchInsurances,
  requestSearchInsurances,
} from 'redux/modules/insurances';
import {
  requestFetchPatients,
  requestSearchPatients,
} from 'redux/modules/patients';
import {
  requestFetchCheckNumbers,
  requestSearchCheckNumbers,
} from 'redux/modules/checkNumbers';
import { accountLoginFail } from 'redux/modules/account';
import {
  requestFetchWorkflowStatuses,
  requestSearchWorkflowStatuses,
} from 'redux/modules/workflowStatuses';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';
import orderStates from 'config/orderStates';
import orderLineTypes from 'config/orderLineTypes';

const styles = theme => ({
  root: {
    position: 'relative',
    height: '100%',
    minHeight: 500,
    boxSizing: 'border-box',
    padding: 24,
    // borderRight: `1px solid ${theme.palette.primary.A400}`,
    [theme.breakpoints.down('sm')]: {
      padding: 8,
    },
  },
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  gridItemShrink: {
    flexShrink: 1,
  },
  gridItemAutosuggest: {
    maxWidth: 245,
    flexBasis: 245,
  },
  iconRefresh: {
    width: 16,
    height: 16,
    margin: '0 8px 0 0',
    verticalAlign: 'top',
  },
  iconCopy: {
    width: 20,
    height: 20,
    margin: '0 6px 0 0',
    verticalAlign: 'top',
  },
  iconTrashCan: {
    width: 16,
    height: 16,
    margin: '0 6px 0 0',
    verticalAlign: 'top',
  },
  header: {
    paddingBottom: 24,
    marginBottom: 12,
    borderBottom: `1px solid ${theme.palette.primary.A400}`
  },
  linkButton: {
    marginBottom: 8,
  },
  formControlLabel: {
    width: 152,
    [theme.breakpoints.down('sm')]: {
      width: 120,
    },
  },
});

class ReportsCreateEditFormAside extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      reportNameCreateDialogOpen: false,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteButtonClick = this.onDeleteButtonClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onReportTemplateClone = this.onReportTemplateClone.bind(this);
    this.onReportNameCreateDialogClose = this.onReportNameCreateDialogClose.bind(this);
    this.onReportNameCreateDialogSubmitSuccess = this.onReportNameCreateDialogSubmitSuccess.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onDeleteButtonClick(event) {
    event.stopPropagation();
    this.setState({ anchorEl: event.currentTarget });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { actions, initialValues, history } = this.props;
    this.setState({ anchorEl: null });
    actions.deleteReport({ ID: initialValues.ID });
    history.push('/reports/custom/create');
  }

  onReportTemplateClone() {
    this.setState({ reportNameCreateDialogOpen: true });
  }

  onReportNameCreateDialogClose() {
    this.setState({ reportNameCreateDialogOpen: false });
  }

  onReportNameCreateDialogSubmitSuccess(values) {
    const { handleSubmit, onSubmitCustom } = this.props;
    setTimeout(() => {
      handleSubmit(onSubmitCustom(true, values))();
    }, 500);
  }

  render() {
    const {
      account,
      variant,
      change,
      history,
      reset,
      classes,
      actions,
      error,
      filters,
      handleSubmit,
      initialValues,
      onSubmitCustom,
    } = this.props;
    const { anchorEl, reportNameCreateDialogOpen } = this.state;
    const open = Boolean(anchorEl);
    const remainingOrderStates = filters['Workflow State ID'] && filters['Workflow State ID'].length > 0
      ? orderStates.filter(item =>
        filters['Workflow State ID'].indexOf(item.ID) === -1)
      : orderStates;
    const selectedWorkflowStatusIDs = filters['Workflow Status ID'] || [];
    const remainingOrderLineTypes = filters['Report Order Line Type'] && filters['Report Order Line Type'].length > 0
      ? orderLineTypes.filter(item =>
        filters['Report Order Line Type'].indexOf(item) === -1)
      : orderLineTypes;
    return (
      <Paper className={classes.root}>
        
        {variant === 'claim' ? (
          <Fragment>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item>
                <Link
                  color="primary"
                  bold
                  onClick={() => {
                    reset();
                  }}
                >
                  <IconRefresh className={classes.iconRefresh} />
                  Reset
                </Link>
              </Grid>
            </Grid>
            <Typography
              variant="subheading"
              color="secondary"
              gutterBottom
            >
              Claim Submission Date:
            </Typography>
            <Grid
              container
              justify="flex-start"
              alignItems="flex-end"
              flex
              spacing={24}
            >
              <Grid item>
                <FormControl component="fieldset" className={classes.formControlLabel}>
                  <Field
                    name="claimDateOfCreationType"
                    component={RadioGroupField}
                    direction="column"
                  >
                    <FormControlLabel
                      value="Date range"
                      control={<Radio color="primary" />}
                      label="Date range"
                    />
                  </Field>
                </FormControl>
              </Grid>
              <Grid item xs={7} md={4}>
                <Fields
                  id="Claim Date of Creation"
                  names={[
                    'Claim Date of Creation From',
                    'Claim Date of Creation To',
                  ]}
                  component={DatepickerField}
                  validate={[validate.required, validate.date]}
                  normalize={{
                    'Claim Date of Creation From': normalize.date,
                    'Claim Date of Creation To': normalize.date,
                  }}
                  fullWidth
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  variant="range"
                  startDateName="Claim Date of Creation From"
                  endDateName="Claim Date of Creation To"
                  rangeButtons
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              justify="flex-start"
              alignItems="flex-end"
              flex
              spacing={24}
            >
              <Grid item>
                <FormControl component="fieldset" className={classes.formControlLabel}>
                  <Field
                    name="claimDateOfCreationType"
                    component={RadioGroupField}
                    direction="column"
                  >
                    <FormControlLabel
                      value="In last"
                      control={<Radio color="primary" />}
                      label="In last"
                    />
                  </Field>
                </FormControl>
              </Grid>
              <Grid item>
                <Grid
                  container
                  spacing={24}
                  wrap="nowrap"
                >
                  <Grid
                    className={classes.gridItemShrink}
                    item
                  >
                    <Field
                      id="Step Number"
                      name="Step Number"
                      label="Number"
                      component={TextField}
                      errorPlacement="top"
                      normalize={normalize.number}
                      fullWidth
                    />
                  </Grid>
                  <Grid item>
                    <FormControl role="group">
                      <FormLabel component="legend">Step Value</FormLabel>
                      <Field
                        name="Step Value"
                        component={RadioGroupField}
                      >
                        <RadioButtonField value="Week" label="Week" />
                        <RadioButtonField value="Month" label="Month" />
                        <RadioButtonField value="Year" label="Year" />
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Prescriber"
                  names={[
                    'Prescriber Name',
                    'Prescriber',
                  ]}
                  autosuggestFieldName="Prescriber Name"
                  fieldArrayName="Prescriber"
                  placeholder="-Select Prescriber-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  renderInputContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Prescriber', {
                      value: suggestion.ID,
                      label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Prescriber ID'] && filters['Prescriber ID'].length > 0)
                      ? requestSearchPrescribers({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Full Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Prescriber ID'] && filters['Prescriber ID'].length > 0
                            ? filters['Prescriber ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchPrescribers({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Sales Person"
                  names={[
                    'Sales Representative Name',
                    'Sales Representative',
                  ]}
                  autosuggestFieldName="Sales Representative Name"
                  fieldArrayName="Sales Representative"
                  placeholder="-Select Sales-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  renderInputContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Sales Representative', {
                      value: suggestion.ID,
                      label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Sales Representative ID'] && filters['Sales Representative ID'].length > 0)
                      ? requestSearchSalesRepresentatives({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Full Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Sales Representative ID'] && filters['Sales Representative ID'].length > 0
                            ? filters['Sales Representative ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchSalesRepresentatives({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Referral Source"
                  names={[
                    'Referral Source Name',
                    'Referral Source',
                  ]}
                  autosuggestFieldName="Referral Source Name"
                  fieldArrayName="Referral Source"
                  placeholder="-Select Referral-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    suggestion['Name of source']}
                  renderInputContent={suggestion =>
                    suggestion['Name of source']}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Referral Source', {
                      value: suggestion.ID,
                      label: suggestion['Name of source'],
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Referral Source ID'] && filters['Referral Source ID'].length > 0)
                      ? requestSearchReferralSources({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Name of source',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Referral Source ID'] && filters['Referral Source ID'].length > 0
                            ? filters['Referral Source ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchReferralSources({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Payer"
                  names={[
                    'Insurance Info Name',
                    'Insurance Info',
                  ]}
                  autosuggestFieldName="Insurance Info Name"
                  fieldArrayName="Insurance Info"
                  placeholder="-Select Payer-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion => suggestion['Payer Name']}
                  renderInputContent={suggestion => suggestion['Payer Name']}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Insurance Info', {
                      value: suggestion.ID,
                      label: suggestion['Payer Name'],
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Insurance Info ID'] && filters['Insurance Info ID'].length > 0)
                      ? requestSearchInsurances({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Payer Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Insurance Info ID'] && filters['Insurance Info ID'].length > 0
                            ? filters['Insurance Info ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchInsurances({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Patients"
                  names={[
                    'Patient Name',
                    'Patient',
                  ]}
                  autosuggestFieldName="Patient Name"
                  fieldArrayName="Patient"
                  placeholder="-Select Patient-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  renderInputContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Patient', {
                      value: suggestion['Record Number'],
                      label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Patient ID'] && filters['Patient ID'].length > 0)
                      ? requestSearchPatients({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Full Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Patient ID'] && filters['Patient ID'].length > 0
                            ? filters['Patient ID'].map(item => ({
                              name: 'Record Number',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchPatients({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
            </Grid>
          </Fragment>
        ) : (
          <Fragment>
            <div className={classes.header}>
              <Grid
                container
                alignItems="flex-end"
                spacing={24}
                // wrap="nowrap"
              >
                {variant === 'edit' &&
                  <Grid
                    className={classes.gridItemShrink}
                    item
                    xs={4}
                  >
                    <Field
                      id="Name"
                      name="Name"
                      label="Report Template Name"
                      component={TextField}
                      validate={[validate.required]}
                      disabled={Object.keys(filters).length === 0}
                      fullWidth
                    />
                  </Grid>
                }
                <Grid item>
                  <Typography
                    variant="subheading"
                    color="secondary"
                    gutterBottom
                  >
                    {variant === 'edit' ? 'or' : 'Create Report or'}
                  </Typography>
                </Grid>
                <Grid
                  className={classes.gridItemAutosuggest}
                  item
                  xs={3}
                >
                  <Field
                    name="Report Template Name"
                    id="Report Template Name"
                    autosuggestFieldName="Patient Name"
                    fieldArrayName="Patient"
                    placeholder={variant === 'edit' ? 'Search Other Template' : 'Search Existing Template'}
                    component={SelectAutosuggestField}
                    fullWidth
                    renderSuggestionContent={suggestion => suggestion.Name}
                    renderInputContent={suggestion => suggestion.Name}
                    onSuggestionSelected={(suggestion) => {
                      change('Report Template ID', suggestion.ID);
                      change('Selected Report Template Name', suggestion.Name);
                      return suggestion.Name;
                    }}
                    fetchSuggestions={({ value, limit, offset }) => 
                      value.length > 0
                        ? requestSearchReports({
                          filters: [{
                            name: 'Name',
                            operation: 'startswith',
                            val: value,
                            case_sensitivity: false,
                          }],
                          limit,
                          offset,
                        }).then(res => res.data,
                          (err) => {
                            if (err.data.error === 'Need authorisation') {
                              actions.accountLoginFail();
                            }
                            return err;
                          })
                        : requestFetchReports({
                          limit,
                          offset,
                        }).then(res => res.data,
                          (err) => {
                            if (err.data.error === 'Need authorisation') {
                              actions.accountLoginFail();
                            }
                            return err;
                          })
                    }
                    {...variant === 'edit' ? {
                      onListAdornmentClick: () => {
                        history.push('/reports/custom/create');
                      },
                      listAdornmentText: 'Create New Report Template',
                    } : {}}
                  />
                </Grid>
                <Grid item>
                  <Link
                    className={classes.linkButton}
                    color="primary"
                    bold
                    onClick={() => {
                      if (variant === 'edit') {
                        history.push('/reports/custom/create');
                      } else {
                        reset();
                      }
                    }}
                  >
                    <IconRefresh className={classes.iconRefresh} />
                    Reset
                  </Link>
                </Grid>
                {variant === 'edit' &&
                  <Fragment>
                    <Grid item>
                      <Link
                        className={classes.linkButton}
                        color="primary"
                        bold
                        onClick={this.onReportTemplateClone}
                      >
                        <IconCopy className={classes.iconCopy} />
                        Clone
                      </Link>
                      <ReportNameCreateDialog
                        title="Clone Report Template"
                        form="Report-Name-Create"
                        open={reportNameCreateDialogOpen}
                        onClose={this.onReportNameCreateDialogClose}
                        onSubmitSuccess={this.onReportNameCreateDialogSubmitSuccess}
                        initialValues={{
                          Name: `${initialValues.Name} (copy)`,
                        }}
                      />
                    </Grid>
                    {(account.Roles.filter(item =>
                      item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length > 0) &&
                      <Grid item>
                        <Link
                          className={classes.linkButton}
                          color="secondary"
                          bold
                          aria-label="Remove"
                          aria-owns={open ? `${initialValues.ID}-report-remove-menu` : null}
                          aria-haspopup="true"
                          onClick={this.onDeleteButtonClick}
                        >
                          <IconTrashCan className={classes.iconTrashCan} />
                          Delete
                        </Link>
                        <Menu
                          id={`${initialValues.ID}-report-remove-menu`}
                          anchorEl={anchorEl}
                          open={open}
                          onClose={this.onMenuClose}
                          list={false}
                        >
                          <DeleteMenu
                            text="Are you sure you want to delete this report template?"
                            onCancel={this.onDeleteCancel}
                            onSubmit={this.onDeleteSubmit}
                          />
                        </Menu>
                      </Grid>
                    }
                  </Fragment>
                }
              </Grid>
            </div>
            {error &&
              <Grid
                className={classes.gridError}
                container
                spacing={24}
              >
                <Grid
                  item
                  xs={12}
                >
                  <FormError>
                    {error}
                  </FormError>
                </Grid>
              </Grid>
            }
            <Grid
              container
              spacing={24}
              alignItems="center"
            >
              <Grid item>
                <Typography
                  variant="subheading"
                  color="secondary"
                >
                  Filter Orders:
                </Typography>
              </Grid>
              <Grid item>
                <Field
                  id="Not Rejected"
                  name="Not Rejected"
                  label="Active Orders"
                  component={CheckboxField}
                />
              </Grid>
              <Grid item>
                <Field
                  id="Rejected"
                  name="Rejected"
                  label="Rejected Orders"
                  component={CheckboxField}
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={24}
            >
              <Grid item xs={12} md={8}>
                <Fields
                  label="By Patients"
                  names={[
                    'Patient Name',
                    'Patient',
                  ]}
                  autosuggestFieldName="Patient Name"
                  fieldArrayName="Patient"
                  placeholder="-Select Patient-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  renderInputContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Patient', {
                      value: suggestion['Record Number'],
                      label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Patient ID'] && filters['Patient ID'].length > 0)
                      ? requestSearchPatients({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Full Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Patient ID'] && filters['Patient ID'].length > 0
                            ? filters['Patient ID'].map(item => ({
                              name: 'Record Number',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchPatients({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Assigned User"
                  names={[
                    'Assigned User Name',
                    'Assigned User',
                  ]}
                  autosuggestFieldName="Assigned User Name"
                  fieldArrayName="Assigned User"
                  placeholder="-Select User-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  renderInputContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Assigned User', {
                      value: suggestion.ID,
                      label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Assigned User ID'] && filters['Assigned User ID'].length > 0)
                      ? requestSearchUsers({
                        filters: [
                          {
                            name: 'Role',
                            operation: 'ne',
                            val: 'System',
                          },
                          ...value.length > 0
                            ? [{
                              name: 'Full Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Assigned User ID'] && filters['Assigned User ID'].length > 0
                            ? filters['Assigned User ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestSearchUsers({
                        filters: [
                          {
                            name: 'Role',
                            operation: 'ne',
                            val: 'System',
                          },
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Fields
                  id="Date of Creation from/to"
                  names={[
                    'Date of Creation From',
                    'Date of Creation To',
                  ]}
                  label="Date of Creation from/to"
                  component={DatepickerField}
                  validate={[validate.required, validate.date]}
                  normalize={{
                    'Date of Creation From': normalize.date,
                    'Date of Creation To': normalize.date,
                  }}
                  fullWidth
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  variant="range"
                  startDateName="Date of Creation From"
                  endDateName="Date of Creation To"
                  rangeButtons
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  id="Date of Service from/to"
                  names={[
                    'Date of Service From',
                    'Date of Service To',
                  ]}
                  label="Date of Service from/to"
                  component={DatepickerField}
                  validate={[validate.required, validate.date]}
                  normalize={{
                    'Date of Service From': normalize.date,
                    'Date of Service To': normalize.date,
                  }}
                  fullWidth
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  variant="range"
                  startDateName="Date of Service From"
                  endDateName="Date of Service To"
                  rangeButtons
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  id="Reimbs Date from/to"
                  names={[
                    'Reimbs Date From',
                    'Reimbs Date To',
                  ]}
                  label="Reimbs Date from/to"
                  component={DatepickerField}
                  validate={[validate.required, validate.date]}
                  normalize={{
                    'Reimbs Date From': normalize.date,
                    'Reimbs Date To': normalize.date,
                  }}
                  fullWidth
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  variant="range"
                  startDateName="Reimbs Date From"
                  endDateName="Reimbs Date To"
                  rangeButtons
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Workflow Category"
                  names={[
                    'Workflow State Name',
                    'Workflow State',
                  ]}
                  autosuggestFieldName="Workflow State Name"
                  fieldArrayName="Workflow State"
                  placeholder="-Select Category-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion => suggestion.Name}
                  renderInputContent={suggestion => suggestion.Name}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Workflow State', {
                      value: suggestion.ID,
                      label: suggestion.Name,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                    if (offset === 0) {
                      if (value.length > 0) {
                        resolve(remainingOrderStates
                          .filter(item => item.Name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                      } else {
                        resolve(remainingOrderStates);
                      }
                    } else {
                      resolve([]);
                    }
                  }))}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Status"
                  names={[
                    'Workflow Status Name',
                    'Workflow Status',
                  ]}
                  autosuggestFieldName="Workflow Status Name"
                  fieldArrayName="Workflow Status"
                  placeholder="-Select Status-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion => suggestion.Name}
                  renderInputContent={suggestion => suggestion.Name}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Workflow Status', {
                      value: suggestion.ID,
                      label: suggestion.Name,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0 || selectedWorkflowStatusIDs.length > 0
                      || (filters['Workflow State ID'] && filters['Workflow State ID'].length > 0)
                      ? requestSearchWorkflowStatuses({
                        filters: [
                          ...value.length > 0 ? [{
                            name: 'Name',
                            operation: 'contains',
                            val: value,
                            case_sensitivity: false,
                          }] : [],
                          ...selectedWorkflowStatusIDs.map(item => ({
                            name: 'ID',
                            operation: 'ne',
                            val: item,
                          })),
                          ...filters['Workflow State ID'] && filters['Workflow State ID'].length > 0 ? [{
                            name: 'Workflow State ID',
                            operation: 'in',
                            val: filters['Workflow State ID'],
                          }] : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchWorkflowStatuses({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By HCPCS Code"
                  names={[
                    'HCPC Code Name',
                    'HCPC Code',
                  ]}
                  autosuggestFieldName="HCPC Code Name"
                  fieldArrayName="HCPC Code"
                  placeholder="-Select HCPCS Code-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion => suggestion['HCPC S Code']}
                  renderInputContent={suggestion => suggestion['HCPC S Code']}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected HCPC Code', {
                      value: suggestion.ID,
                      label: suggestion['HCPC S Code'],
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['HCPC S Code ID'] && filters['HCPC S Code ID'].length > 0)
                      ? requestSearchHCPCSCodes({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'HCPC S Code',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['HCPC S Code ID'] && filters['HCPC S Code ID'].length > 0
                            ? filters['HCPC S Code ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchHCPCSCodes({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  normalize={normalize.hcpcCode}
                  keepOpenAfterSelection
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Type"
                  names={[
                    'Report Order Line Type Name',
                    'Report Order Line Type',
                  ]}
                  autosuggestFieldName="Report Order Line Type Name"
                  fieldArrayName="Report Order Line Type"
                  placeholder="-Select Type-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion => suggestion}
                  renderInputContent={suggestion => suggestion}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Report Order Line Type', {
                      value: suggestion,
                      label: suggestion,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                    if (offset === 0) {
                      if (value.length > 0) {
                        resolve(remainingOrderLineTypes
                          .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                      } else {
                        resolve(remainingOrderLineTypes);
                      }
                    } else {
                      resolve([]);
                    }
                  }))}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Prescriber"
                  names={[
                    'Prescriber Name',
                    'Prescriber',
                  ]}
                  autosuggestFieldName="Prescriber Name"
                  fieldArrayName="Prescriber"
                  placeholder="-Select Prescriber-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  renderInputContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Prescriber', {
                      value: suggestion.ID,
                      label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Prescriber ID'] && filters['Prescriber ID'].length > 0)
                      ? requestSearchPrescribers({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Full Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Prescriber ID'] && filters['Prescriber ID'].length > 0
                            ? filters['Prescriber ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchPrescribers({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Sales Person"
                  names={[
                    'Sales Representative Name',
                    'Sales Representative',
                  ]}
                  autosuggestFieldName="Sales Representative Name"
                  fieldArrayName="Sales Representative"
                  placeholder="-Select Sales-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  renderInputContent={suggestion =>
                    `${suggestion['First Name']} ${suggestion['Last Name']}`}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Sales Representative', {
                      value: suggestion.ID,
                      label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Sales Representative ID'] && filters['Sales Representative ID'].length > 0)
                      ? requestSearchSalesRepresentatives({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Full Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Sales Representative ID'] && filters['Sales Representative ID'].length > 0
                            ? filters['Sales Representative ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchSalesRepresentatives({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
            </Grid>
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Referral Source"
                  names={[
                    'Referral Source Name',
                    'Referral Source',
                  ]}
                  autosuggestFieldName="Referral Source Name"
                  fieldArrayName="Referral Source"
                  placeholder="-Select Referral-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion =>
                    suggestion['Name of source']}
                  renderInputContent={suggestion =>
                    suggestion['Name of source']}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Referral Source', {
                      value: suggestion.ID,
                      label: suggestion['Name of source'],
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Referral Source ID'] && filters['Referral Source ID'].length > 0)
                      ? requestSearchReferralSources({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Name of source',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Referral Source ID'] && filters['Referral Source ID'].length > 0
                            ? filters['Referral Source ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchReferralSources({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Payer"
                  names={[
                    'Insurance Info Name',
                    'Insurance Info',
                  ]}
                  autosuggestFieldName="Insurance Info Name"
                  fieldArrayName="Insurance Info"
                  placeholder="-Select Payer-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion => suggestion['Payer Name']}
                  renderInputContent={suggestion => suggestion['Payer Name']}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Insurance Info', {
                      value: suggestion.ID,
                      label: suggestion['Payer Name'],
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Insurance Info ID'] && filters['Insurance Info ID'].length > 0)
                      ? requestSearchInsurances({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Payer Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Insurance Info ID'] && filters['Insurance Info ID'].length > 0
                            ? filters['Insurance Info ID'].map(item => ({
                              name: 'ID',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchInsurances({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Fields
                  label="By Check Number"
                  names={[
                    'Check Book Sheet Code',
                    'Check Book Sheet',
                  ]}
                  autosuggestFieldName="Check Book Sheet Code"
                  fieldArrayName="Check Book Sheet"
                  placeholder="-Select Check Number-"
                  component={MultiselectAutosuggestField}
                  renderSuggestionContent={suggestion => suggestion['Check Number']}
                  renderInputContent={suggestion => suggestion['Check Number']}
                  onSuggestionSelected={(suggestion) => {
                    change('Selected Check Book Sheet', {
                      value: suggestion.ID,
                      label: suggestion['Check Number'],
                    });
                    return '';
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      || (filters['Check Book Sheet'] && filters['Check Book Sheet'].length > 0)
                      ? requestSearchCheckNumbers({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Check Number',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                          ...filters['Check Book Sheet'] && filters['Check Book Sheet'].length > 0
                            ? filters['Check Book Sheet'].map(item => ({
                              name: 'Check Number',
                              operation: 'ne',
                              val: item,
                            }))
                            : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchCheckNumbers({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  keepOpenAfterSelection
                />
              </Grid>
            </Grid>
          </Fragment>
        )}
      </Paper>
    );
  }
}

ReportsCreateEditFormAside.propTypes = {
  variant: PropTypes.oneOf(['create', 'edit', 'claim']).isRequired,
  change: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    grid: PropTypes.string.isRequired,
    gridItemShrink: PropTypes.string.isRequired,
    iconRefresh: PropTypes.string.isRequired,
  }).isRequired,
  error: PropTypes.string,
  filters: PropTypes.shape({
    Name: PropTypes.string,
    'Date of Service From': PropTypes.string,
    'Date of Service To': PropTypes.string,
    'Date of Creation From': PropTypes.string,
    'Date of Creation To': PropTypes.string,
    'Workflow State': PropTypes.arrayOf(PropTypes.string),
    'Workflow Status': PropTypes.arrayOf(PropTypes.string),
    'Report Order Line Type': PropTypes.arrayOf(PropTypes.string),
  }),
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
};

ReportsCreateEditFormAside.defaultProps = {
  filters: {},
  error: undefined,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail, deleteReport }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(
  withStyles(styles)(ReportsCreateEditFormAside),
));



// WEBPACK FOOTER //
// ./src/components/reports/ReportsCreateEditForm/ReportsCreateEditFormAside.js